import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useEditMode } from '../AuthContext';
import { getFirestore, collection, getDocs, query, orderBy, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { app } from '../firebase';

interface Application {
  name: string;
  charClass: string;
  spec: string;
  contact: string;
  discordUsername: string;
  experience: string;
  message: string;
  submitted: any;
  docId: string;
  contacted?: boolean;
  raidNights?: {
    tuesday?: { apply: boolean; available: string };
    thursday?: { apply: boolean; available: string };
    sunday?: { apply: boolean; available: string };
  };
}

function toSpecIconPath(className: string, spec: string, ext: string = 'png') {
  return `/images/spec/${className.toLowerCase().replace(/ |-/g, '')}/${spec.toLowerCase().replace(/ |-/g, '')}.${ext}`;
}
const SpecIcon: React.FC<{ className: string; spec: string }> = ({ className, spec }) => {
  const [imgSrc, setImgSrc] = React.useState(toSpecIconPath(className, spec, 'webp'));
  return (
    <img
      src={imgSrc}
      alt={`${spec} icon`}
      className="w-5 h-5 inline-block"
      onError={e => {
        if (imgSrc.endsWith('.webp')) {
          setImgSrc(toSpecIconPath(className, spec, 'png'));
        } else if (imgSrc.endsWith('.png')) {
          setImgSrc(toSpecIconPath(className, spec, 'jpg'));
        } else {
          (e.target as HTMLImageElement).style.display = 'none';
        }
      }}
    />
  );
};

const AdminApplications: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const { editMode } = useEditMode();
  const navigate = useNavigate();
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') {
      navigate('/login');
      return;
    }
    const fetchApplications = async () => {
      setLoading(true);
      try {
        const db = getFirestore(app);
        const q = query(collection(db, 'applications'), orderBy('submitted', 'desc'));
        const querySnapshot = await getDocs(q);
        const apps: Application[] = querySnapshot.docs.map(docSnap => {
          const data = docSnap.data();
          return {
            name: data.name || '',
            charClass: data.charClass || '',
            spec: data.spec || '',
            contact: data.contact || '',
            discordUsername: data.discordUsername || '',
            experience: data.experience || '',
            message: data.message || '',
            submitted: data.submitted ? (data.submitted.toDate ? data.submitted.toDate().toLocaleDateString() : data.submitted) : '',
            docId: docSnap.id,
            contacted: data.contacted || false,
            raidNights: data.raidNights || undefined,
          };
        });
        setApplications(apps);
      } catch (err) {
        setApplications([]);
      }
      setLoading(false);
    };
    fetchApplications();
  }, [user, authLoading, navigate]);

  if (authLoading) {
    return <div className="page-container flex items-center justify-center text-center text-text-secondary">Checking authentication...</div>;
  }

  const handleDecline = async (docId: string) => {
    const db = getFirestore(app);
    await deleteDoc(doc(db, 'applications', docId));
    setApplications(applications => applications.filter((_, i) => applications[i].docId !== docId));
  };

  const handleContacted = async (docId: string) => {
    const db = getFirestore(app);
    await updateDoc(doc(db, 'applications', docId), { contacted: true });
    setApplications(applications => applications.map(app => app.docId === docId ? { ...app, contacted: true } : app));
  };

  return (
    <div className="page-container bg-surface">
      <div className="max-w-6xl mx-auto px-4 pb-12">
        <h1 className="text-4xl font-bold mb-8 text-center">
          <span className="gradient-text">Recruitment Applications</span>
        </h1>
        {loading ? (
          <div className="text-center text-text-secondary">Loading applications...</div>
        ) : applications.length === 0 ? (
          <div className="text-center text-text-secondary">No applications submitted yet.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {applications.map((app, i) => (
              <div key={i} className={`glass p-6 rounded-xl ${app.contacted ? 'border-2 border-green-400 bg-green-900/10' : ''}`}>
                <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 gap-1 md:gap-0">
                  <div className="font-bold text-lg text-cyan-400 break-words md:text-xl">{app.name}</div>
                  <div className="text-xs md:text-sm text-text-secondary md:ml-4 whitespace-nowrap">Submitted: {app.submitted}</div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
                  <div><span className="font-semibold">Class:</span> {app.charClass}</div>
                  <div className="flex items-center gap-2 whitespace-nowrap">
                    <span className="font-semibold">Spec:</span>
                    <span className="flex items-center gap-1 min-w-[20px]">
                      <SpecIcon className={app.charClass} spec={app.spec} />
                    </span>
                    <span>{app.spec}</span>
                  </div>
                  <div><span className="font-semibold">BattleTag:</span> {app.contact}</div>
                  <div><span className="font-semibold">Discord:</span> {app.discordUsername || '—'}</div>
                </div>
                <div className="mb-2"><span className="font-semibold">Experience:</span> {app.experience}</div>
                {app.message && <div className="mb-2"><span className="font-semibold">Message:</span> {app.message}</div>}
                {app.raidNights && (
                  <div className="mb-2">
                    <span className="font-semibold">Raid Night Availability:</span>
                    <ul className="ml-4 mt-1 list-disc text-sm">
                      {app.raidNights.tuesday?.apply && (
                        <li>Tuesday (Mythic): {app.raidNights.tuesday.available === 'yes' ? 'Available' : app.raidNights.tuesday.available === 'no' ? 'Not Available' : 'No response'}</li>
                      )}
                      {app.raidNights.thursday?.apply && (
                        <li>Thursday (Mythic): {app.raidNights.thursday.available === 'yes' ? 'Available' : app.raidNights.thursday.available === 'no' ? 'Not Available' : 'No response'}</li>
                      )}
                      {app.raidNights.sunday?.apply && (
                        <li>Sunday (Heroic/AOTC): {app.raidNights.sunday.available === 'yes' ? 'Available' : app.raidNights.sunday.available === 'no' ? 'Not Available' : 'No response'}</li>
                      )}
                    </ul>
                  </div>
                )}
                <div className="flex gap-2 mt-2">
                  {user && user.role === 'admin' && editMode && (
                    <>
                      <button
                        className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition"
                        onClick={() => handleDecline(app.docId)}
                      >
                        Decline
                      </button>
                      <button
                        className={`px-4 py-2 rounded transition ${app.contacted ? 'bg-green-500 text-white cursor-not-allowed' : 'bg-cyan-500 text-white hover:bg-cyan-600'}`}
                        onClick={() => handleContacted(app.docId)}
                        disabled={app.contacted}
                      >
                        {app.contacted ? 'Contacted' : 'Mark as Contacted'}
                      </button>
                    </>
                  )}
                </div>
                {app.contacted && (
                  <div className="mt-2 text-green-400 font-semibold">Applicant has been contacted.</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminApplications; 